import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { Col, Row } from "reactstrap";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const legendItemStyle = {
  alignItems: "center",
  display: "flex",
  gap: 6,
  justifyContent: "center",
  lineHeight: 1.2,
};

const legendSwatchStyle = (backgroundColor) => ({
  backgroundColor,
  display: "inline-block",
  flex: "0 0 10px",
  height: 10,
  width: 10,
});

function BacktestBarChart({ data: assetData, theme }) {
  const [evalmetrics, setEvalmetrics] = useState([]);
  const [seriesConfig, setSeriesConfig] = useState({});

  const strategyLabel = seriesConfig?.index1 || "MQE Strategy";
  const assetId = assetData?.id;

  const fetchData = useCallback(async (type) => {
    try {
      const url =
        type === "assets"
          ? `${process.env.REACT_APP_BASE_URL}/assets/${assetId}/evalmetrics?skip=0&limit=10`
          : `${process.env.REACT_APP_BASE_URL}/portfolios/${assetId}/evalmetrics?skip=0&limit=10`;
      const response = await axios.get(url);
      if (response.status === 200) {
        setEvalmetrics(response?.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, [assetId]);

  const fetchConfigs = useCallback(async (type) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/display-configs/${assetId}?scope=${type}`,
        {
          headers: {
            Authorization: `Bearer ${process.env.REACT_APP_TOKEN}`,
          },
        }
      );
      if (response.status === 200) {
        setSeriesConfig(response.data);
      }
    } catch (error) {
      console.error("Error fetching configs:", error);
      setSeriesConfig({});
    }
  }, [assetId]);

  const fetchPortFolioData = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/portfolios/?skip=0&limit=1000`
      );
      if (response.status === 200) {
        const isPortfolio =
          response?.data?.data?.length &&
          response.data.data.some((d) => d.id === assetId);
        const type = isPortfolio ? "portfolio" : "assets";
        fetchData(type);
        fetchConfigs(isPortfolio ? "portfolio" : "asset");
      }
    } catch (error) {
      console.error("Error fetching portfolio data:", error);
    }
  }, [assetId, fetchData, fetchConfigs]);

  useEffect(() => {
    if (assetId) {
      fetchPortFolioData();
    }
  }, [assetId, fetchPortFolioData]);

  return (
    <Row>
      {evalmetrics.slice(0, 3).map((item, index) => {
        const benchmarkLabel = item?.hfrititle || "HFRI 500";
        return (
          <Col sm="12" md="12" lg="4" key={item?.metric || index}>
            <ResponsiveContainer width="100%" height={170}>
              <BarChart
                width={450}
                height={200}
                stackOffset="sign"
                className="bc"
                data={[
                  {
                    name: item?.metric,
                    [strategyLabel]: item?.mqe,
                    [benchmarkLabel]: item?.hfri,
                  },
                ]}
              >
                <CartesianGrid vertical={false} horizontal={false} />
                <XAxis
                  style={{ fill: theme === "light" ? null : "white" }}
                  dataKey="name"
                />
                <YAxis />
                <Tooltip wrapperClassName="custom-tooltip" />
                <ReferenceLine y={0} stroke="#000" />
                <Bar dataKey={strategyLabel} fill="#005687" />
                <Bar dataKey={benchmarkLabel} fill="#FBC138" />
              </BarChart>
            </ResponsiveContainer>
            <div
              style={{
                color: theme === "light" ? "#333" : "white",
                display: "grid",
                fontSize: 11,
                gap: 4,
                justifyItems: "center",
                marginTop: 4,
                minHeight: 34,
                textAlign: "center",
              }}
            >
              <span style={legendItemStyle}>
                <span style={legendSwatchStyle("#005687")} />
                <span>{strategyLabel}</span>
              </span>
              <span style={legendItemStyle}>
                <span style={legendSwatchStyle("#FBC138")} />
                <span>{benchmarkLabel}</span>
              </span>
            </div>
          </Col>
        );
      })}
    </Row>
  );
}

export default BacktestBarChart;
